import DEPARTMENT_CONFIG, {
  getDepartmentBadgeColor,
  getDepartmentColor,
  getDepartmentHex,
  getDepartmentShortName,
} from './departmentConfig';

/**
 * Canonical trade → color mapping.
 *
 * Keys are **exact** `trade_code` values as stored on TradeCategory.
 *
 * Rule: when a trade shares identity with a department (exact match on
 * `trade_code` OR trade name against a `department_name`), reuse that
 * department's unique color family — HR already associates that color with
 * the org unit (e.g. Safety, DfMA). Standalone trades get their own
 * InfoBadge-compatible color key chosen for semantic association
 * (water → blue, electrical → amber, etc.).
 *
 * No per-trade icons — `trade_code` is already the dense scan label.
 *
 * UI MUST render trades via `<TradeBadge>` (`components/UI/TradeBadge.js`).
 */

/** Full trade name by code (tooltips / title). */
const TRADE_NAME_BY_CODE = {
  CHWP: 'Chilled Water Piping',
  DfMA: 'Design for Manufacturing and Assembly',
  Duct: 'Ductwork',
  Electrical: 'Electrical',
  Factory: 'Factory',
  Logistics: 'Logistics',
  'Ref Pipe': 'Refrigeration Piping',
  Safety: 'Safety',
  'S&M': 'Service & Maintenance',
  'T&C': 'Testing & Commissioning',
};

/** Reverse lookup: full name → trade_code (exact). */
const TRADE_CODE_BY_NAME = Object.fromEntries(
  Object.entries(TRADE_NAME_BY_CODE).map(([code, name]) => [name, code])
);

/**
 * Own colors for trades that do NOT align to a department.
 * Values MUST be InfoBadge color keys.
 *
 * Semantic associations (HR / site intuition), not arbitrary uniqueness:
 *   CHWP      → water          → blue
 *   Ref Pipe  → cold / refrigerant → cyan
 *   Electrical → hazard yellow  → amber
 *   Duct      → air / HVAC duct → sky   (NOT slate — slate≈gray at chip size)
 *   Factory   → industrial      → gray
 *   Logistics → transport       → orange
 *   S&M       → service tools   → violet
 *   T&C       → test / pass     → green
 *
 * Dense-chip uniqueness is mandatory: slate vs gray fails HR scan tests.
 */
const TRADE_OWN_BADGE_COLOR = {
  CHWP: 'blue',
  'Ref Pipe': 'cyan',
  Electrical: 'amber',
  Duct: 'sky',
  Factory: 'gray',
  Logistics: 'orange',
  'S&M': 'violet',
  'T&C': 'green',
};

const OWN_HEX = {
  blue: '#3b82f6',
  cyan: '#06b6d4',
  amber: '#f59e0b',
  sky: '#0ea5e9',
  gray: '#6b7280',
  orange: '#f97316',
  violet: '#8b5cf6',
  green: '#16a34a',
};

/**
 * Resolve a department name this trade should inherit color from.
 * Exact match only — never substring matching.
 */
function getAlignedDepartmentName(tradeCode, tradeName) {
  if (tradeCode && DEPARTMENT_CONFIG[tradeCode]) return tradeCode;
  if (tradeName && DEPARTMENT_CONFIG[tradeName]) return tradeName;
  const knownName = tradeCode ? TRADE_NAME_BY_CODE[tradeCode] : null;
  if (knownName && DEPARTMENT_CONFIG[knownName]) return knownName;
  return null;
}

/**
 * InfoBadge color key for a trade (e.g. Safety → 'red' via department).
 *
 * @param {string} tradeCode
 * @param {string} [tradeName]
 * @returns {string}
 */
export function getTradeBadgeColor(tradeCode, tradeName) {
  if (!tradeCode && !tradeName) return 'gray';

  const resolvedCode = tradeCode || TRADE_CODE_BY_NAME[tradeName] || '';
  const resolvedName = tradeName || (resolvedCode ? TRADE_NAME_BY_CODE[resolvedCode] : '');

  const alignedDept = getAlignedDepartmentName(resolvedCode, resolvedName);
  if (alignedDept) return getDepartmentBadgeColor(alignedDept);

  if (resolvedCode && TRADE_OWN_BADGE_COLOR[resolvedCode]) {
    return TRADE_OWN_BADGE_COLOR[resolvedCode];
  }
  return 'gray';
}

/**
 * Full trade display name for tooltips (falls back to trade_code).
 */
export function getTradeDisplayName(tradeCode, tradeName) {
  if (tradeName) return tradeName;
  if (!tradeCode) return '';
  return TRADE_NAME_BY_CODE[tradeCode] || tradeCode;
}

/**
 * Dense chip label — prefer trade_code (already short).
 */
export function getTradeShortLabel(tradeCode, tradeName) {
  if (tradeCode) return tradeCode;
  if (tradeName && TRADE_CODE_BY_NAME[tradeName]) return TRADE_CODE_BY_NAME[tradeName];
  return tradeName || '';
}

/**
 * Normalize ambiguous API values into { code, name }.
 * Prefer known trade_code keys; fall back to full-name reverse lookup.
 */
export function resolveTradeIdentity(tradeCode, tradeName, trade) {
  let code = tradeCode || '';
  let name = tradeName || '';

  if (trade != null) {
    if (typeof trade === 'string') {
      if (TRADE_NAME_BY_CODE[trade]) {
        code = code || trade;
        name = name || TRADE_NAME_BY_CODE[trade];
      } else if (TRADE_CODE_BY_NAME[trade]) {
        code = code || TRADE_CODE_BY_NAME[trade];
        name = name || trade;
      } else if (!code && !name) {
        code = trade;
      }
    } else if (typeof trade === 'object') {
      code = code || trade.trade_code || '';
      name = name
        || trade.name
        || trade.trade_category_name
        || trade.trade_name
        || '';
    }
  }

  if (!code && name && TRADE_CODE_BY_NAME[name]) {
    code = TRADE_CODE_BY_NAME[name];
  }
  if (!name && code && TRADE_NAME_BY_CODE[code]) {
    name = TRADE_NAME_BY_CODE[code];
  }

  return { code, name };
}

/**
 * Department color object when aligned; otherwise null.
 */
export function getTradeColor(tradeCode, tradeName) {
  const alignedDept = getAlignedDepartmentName(tradeCode, tradeName);
  if (alignedDept) return getDepartmentColor(alignedDept);
  return null;
}

/**
 * Hex for charts — department hex when aligned, else own-trade palette.
 */
export function getTradeHex(tradeCode, tradeName) {
  const alignedDept = getAlignedDepartmentName(tradeCode, tradeName);
  if (alignedDept) return getDepartmentHex(alignedDept);
  const key = getTradeBadgeColor(tradeCode, tradeName);
  return OWN_HEX[key] || OWN_HEX.gray;
}

/**
 * True when this trade reuses a department's color identity.
 */
export function isTradeAlignedToDepartment(tradeCode, tradeName) {
  return Boolean(getAlignedDepartmentName(tradeCode, tradeName));
}

/**
 * True when a trade chip would duplicate the department chip for this employee.
 *
 * HR mental model: Department is the primary org signal. Trade only earns a
 * second chip when it adds disambiguation (e.g. Teams → I&C / S&M). When the
 * trade_code (or trade name) matches the department name or shortName
 * (Safety → Safety, DfMA → DfMA), suppress the trade chip.
 *
 * Comparison is case-insensitive trim; still exact-token (no substring).
 *
 * @param {string} [departmentName] - StaffUser department_name
 * @param {string} [tradeCode]
 * @param {string} [tradeName]
 * @param {object|string} [trade] - TradeCategory-like object or string
 * @returns {boolean}
 */
export function isTradeRedundantWithDepartment(departmentName, tradeCode, tradeName, trade) {
  if (!departmentName) return false;

  const { code, name } = resolveTradeIdentity(tradeCode, tradeName, trade);
  if (!code && !name) return false;

  const deptLabels = new Set(
    [departmentName, getDepartmentShortName(departmentName)]
      .filter(Boolean)
      .map((s) => s.trim().toLowerCase())
  );

  return [code, name]
    .filter(Boolean)
    .some((token) => deptLabels.has(String(token).trim().toLowerCase()));
}

export default {
  TRADE_NAME_BY_CODE,
  TRADE_OWN_BADGE_COLOR,
};
